/**
 * Canonical market definitions shared across the application.
 *
 * Internal values use short ISO-style codes:
 *   global → English (no specific region)
 *   jp     → Japan
 *   kr     → Korea
 *   id     → Indonesia
 *   in     → India
 *
 * Taiwan and Hong Kong were consolidated into 'jp' for CJK character-limit purposes
 * when the market list was reduced to the five supported markets in the spec.
 */

export const MARKETS = [
  { value: 'global', label: 'Global (English)' },
  { value: 'jp',     label: 'Japan' },
  { value: 'kr',     label: 'Korea' },
  { value: 'id',     label: 'Indonesia' },
  { value: 'in',     label: 'India' },
] as const;

export type MarketValue = typeof MARKETS[number]['value'];

/**
 * Maps each market code to its primary BCP 47 language tag.
 * Used to surface templates whose `language` field matches the active market.
 */
export const MARKET_LANGUAGE: Record<string, string> = {
  global: 'en',
  jp: 'ja',
  kr: 'ko',
  id: 'id',
  in: 'hi',
};

/**
 * Maps legacy long-form market values (stored in Firestore before the rename)
 * to the current canonical short codes so existing data continues to work.
 *
 * taiwan / hongkong are folded into 'jp' because:
 * - They shared the same CJK character limits as Japan.
 * - They are no longer in the supported market list per spec.
 */
export const MARKET_ALIASES: Record<string, string> = {
  japan:     'jp',
  korea:     'kr',
  indonesia: 'id',
  india:     'in',
  taiwan:    'jp',
  hongkong:  'jp',
};

/**
 * Normalises a raw market string (e.g. from Firestore) to a canonical value.
 * Unknown values are returned as-is so they remain visible rather than silently dropped.
 */
export function normaliseMarket(raw: string): string {
  return MARKET_ALIASES[raw] ?? raw;
}
